import { IonButton, IonCard, IonCardContent, IonCol, IonLoading, IonRow } from '@ionic/react';
import { DqText } from '../../components/dq-text';
import { JobReportGuestAuthMessages } from '../../intl/JobReportGuestAuthMessages';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Auth } from 'aws-amplify';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from '../../redux/types';
import { AnyAction } from 'redux';
import { authActions } from '../../redux/auth/actions';
import { OneButtonModalContent, OneButtonModal } from '../../components/OneButtonModal';
import { CognitoUser } from '@aws-amplify/auth';
import { NotGuestComponent } from './NotGuestComponent';

export interface GenerateCodeCardProps {
  onCodeRequestSent: (cognitoUser: CognitoUser) => void;
}

/**
 * Contains button to generate a security code so guest can sign in.
 *
 * Initial card that will be seen when user clicks an email guest share link.
 *
 * Optionally allows user to sign in with username and password.
 *
 * @param onCodeRequestSent
 * @constructor
 */
export const GenerateCodeCard: FC<GenerateCodeCardProps> = ({ onCodeRequestSent }) => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const dispatch = useDispatch<ThunkDispatch<AppState, undefined, AnyAction>>();
  const [awaitingCodeGenerationResult, setAwaitingCodeGenerationResult] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<OneButtonModalContent>(['', '']);

  useEffect(() => {
    dispatch(authActions.logOut());
  }, [dispatch]);

  const emailAddress = useMemo(() => {
    const queryPair = history.location.search;
    const urlParams = new URLSearchParams(queryPair);
    return urlParams.get('sharedWith');
  }, [history.location.search]);

  const onGenerateCodeClicked = useCallback(async () => {
    setAwaitingCodeGenerationResult(true);
    if (emailAddress) {
      Auth.signIn(emailAddress)
        .then((cognitoUser: CognitoUser) => {
          setAwaitingCodeGenerationResult(false);
          // Make sure user is signed out. If they are not, then code validation becomes a no-op, i.e. if they enter
          // an invalid security code, they'll be validated by Cognito and able to see the report.
          cognitoUser.signOut();
          onCodeRequestSent(cognitoUser);
          console.log('Auth.signIn successful, challenge name: ', cognitoUser.challengeName);
        })
        .catch(err => {
          setAwaitingCodeGenerationResult(false);
          const errorTitle = formatMessage(JobReportGuestAuthMessages.couldNotGenerateCodeTitle);
          const errorMessage = formatMessage(JobReportGuestAuthMessages.couldNotGenerateCodeMessage);
          setModalContent([errorTitle, errorMessage]);
          console.log('Auth.signIn failed', err);
        });
    } else {
      // This should have been caught earlier, but we'll flag it just in case.
      const errorTitle = formatMessage(JobReportGuestAuthMessages.emailAddressMissingTitle);
      const errorMessage = formatMessage(JobReportGuestAuthMessages.emailAddressMissingMessage);
      setModalContent([errorTitle, errorMessage]);
    }
  }, [emailAddress, formatMessage, onCodeRequestSent]);

  return (
    <IonCard className="content-card">
      <IonLoading isOpen={awaitingCodeGenerationResult} spinner="circles" />
      <OneButtonModal
        isOpen={modalContent[0].length > 0}
        onClose={() => setModalContent(['', ''])}
        content={modalContent}
      />
      <IonCardContent>
        <IonRow>
          <DqText variant="h3" color="dark">
            {formatMessage(JobReportGuestAuthMessages.generateCodeTitle)}
          </DqText>
        </IonRow>
        <IonRow>
          <DqText className="ion-margin-vertical" variant="body" color="dark">
            {formatMessage(JobReportGuestAuthMessages.generateCodeMessage, {
              emailAddress,
            })}
          </DqText>
        </IonRow>
        <IonRow>
          <IonCol className="ion-no-padding ion-margin-vertical">
            <IonButton onClick={onGenerateCodeClicked} fill="solid" color="primary">
              {formatMessage(JobReportGuestAuthMessages.generateCodeButtonLabel)}
            </IonButton>
          </IonCol>
        </IonRow>
        <IonRow className="top-border" />
        <NotGuestComponent />
      </IonCardContent>
    </IonCard>
  );
};
